export type PlayerStats = {
  maxHp: number;
  hp: number;
  speed: number;
  damage: number;
  attackCooldownMs: number;
  attackRange: number;
  magnetRadius: number;
  armor: number;
  vampirism: number;
  hpRegenPerSecond: number;
};

export type UpgradeId =
  | 'serrated-edge'
  | 'wrath-beads'
  | 'deserter-boots'
  | 'iron-cross'
  | 'magnetic-idol';

export type UpgradeTier = 'D' | 'B' | 'A' | 'S' | 'S+' | 'S++';
export type UpgradeIcon = 'sword' | 'rapid-blades' | 'boot' | 'shield' | 'magnet';

export type TierDefinition = {
  id: UpgradeTier;
  multiplier: number;
  weight: number;
  accent: number;
};

export type UpgradeDefinition = {
  id: UpgradeId;
  title: string;
  description: string;
  category: string;
  badgeLabel: string;
  icon: UpgradeIcon;
  accent: number;
  maxRank: number;
  apply: (stats: PlayerStats, multiplier: number) => PlayerStats;
};

export type UpgradeOffer = {
  upgrade: UpgradeDefinition;
  tier: UpgradeTier;
};

export const DEFAULT_STATS: PlayerStats = {
  maxHp: 100,
  hp: 100,
  speed: 165,
  damage: 22,
  attackCooldownMs: 720,
  attackRange: 112,
  magnetRadius: 78,
  armor: 0,
  vampirism: 0.04,
  hpRegenPerSecond: 0.35,
};

// Рабочие вероятности vertical slice. Финальный баланс определяется игровыми тестами.
export const UPGRADE_TIERS: TierDefinition[] = [
  { id: 'D', multiplier: 0.75, weight: 0.33, accent: 0x77756d },
  { id: 'B', multiplier: 1, weight: 0.27, accent: 0xa57a52 },
  { id: 'A', multiplier: 1.25, weight: 0.2, accent: 0x7896a6 },
  { id: 'S', multiplier: 1.6, weight: 0.12, accent: 0xd1ad5d },
  { id: 'S+', multiplier: 2, weight: 0.06, accent: 0xd86f52 },
  { id: 'S++', multiplier: 2.5, weight: 0.02, accent: 0xb477d1 },
];

export function getTierDefinition(tier: UpgradeTier): TierDefinition {
  return UPGRADE_TIERS.find((definition) => definition.id === tier)!;
}

export function rollUpgradeTier(random: () => number = Math.random): UpgradeTier {
  const roll = Math.min(0.999999, Math.max(0, random()));
  let threshold = 0;

  for (const tier of UPGRADE_TIERS) {
    threshold += tier.weight;
    if (roll < threshold) return tier.id;
  }

  return 'S++';
}

export const UPGRADES: UpgradeDefinition[] = [
  {
    id: 'serrated-edge',
    title: 'Зазубренная кромка',
    description: '+8 урона мечом',
    category: 'ОРУЖИЕ',
    badgeLabel: 'УРОН',
    icon: 'sword',
    accent: 0xb85c4f,
    maxRank: 5,
    apply: (stats, multiplier) => ({
      ...stats,
      damage: stats.damage + Math.round(8 * multiplier),
    }),
  },
  {
    id: 'wrath-beads',
    title: 'Стремительный выпад',
    description: 'Атаки на 12% чаще',
    category: 'ТЕМП АТАКИ',
    badgeLabel: 'ТЕМП',
    icon: 'rapid-blades',
    accent: 0xd49a58,
    maxRank: 5,
    apply: (stats, multiplier) => ({
      ...stats,
      attackCooldownMs: Math.max(
        240,
        Math.round(stats.attackCooldownMs * (1 - 0.12 * multiplier)),
      ),
    }),
  },
  {
    id: 'deserter-boots',
    title: 'Шаг сквозь тьму',
    description: '+18 к скорости',
    category: 'ДВИЖЕНИЕ',
    badgeLabel: 'БЕГ',
    icon: 'boot',
    accent: 0x8e9b77,
    maxRank: 5,
    apply: (stats, multiplier) => ({
      ...stats,
      speed: stats.speed + Math.round(18 * multiplier),
    }),
  },
  {
    id: 'iron-cross',
    title: 'Клятва хранительницы',
    description: '+20 HP, лечение и +4% брони',
    category: 'ВЫЖИВАНИЕ',
    badgeLabel: 'HP',
    icon: 'shield',
    accent: 0x9aa4aa,
    maxRank: 5,
    apply: (stats, multiplier) => {
      const healthGain = Math.round(20 * multiplier);
      return {
        ...stats,
        maxHp: stats.maxHp + healthGain,
        hp: Math.min(stats.maxHp + healthGain, stats.hp + healthGain),
        armor: Math.min(0.35, stats.armor + 0.04 * multiplier),
      };
    },
  },
  {
    id: 'magnetic-idol',
    title: 'Жатва осколков',
    description: '+42 к притяжению опыта',
    category: 'ПОДБОР ОПЫТА',
    badgeLabel: 'СБОР',
    icon: 'magnet',
    accent: 0x73bcae,
    maxRank: 5,
    apply: (stats, multiplier) => ({
      ...stats,
      magnetRadius: stats.magnetRadius + Math.round(42 * multiplier),
    }),
  },
];

export type UpgradeRanks = Partial<Record<UpgradeId, number>>;

export type TakenUpgradeBadge = {
  id: UpgradeId;
  label: string;
  rank: number;
  accent: number;
  icon: UpgradeIcon;
};

export function getTakenUpgradeBadges(ranks: UpgradeRanks): TakenUpgradeBadge[] {
  return UPGRADES.flatMap((upgrade) => {
    const rank = ranks[upgrade.id] ?? 0;
    return rank > 0
      ? [{
          id: upgrade.id,
          label: upgrade.badgeLabel,
          rank,
          accent: upgrade.accent,
          icon: upgrade.icon,
        }]
      : [];
  });
}

export function availableUpgrades(ranks: UpgradeRanks): UpgradeDefinition[] {
  return UPGRADES.filter((upgrade) => (ranks[upgrade.id] ?? 0) < upgrade.maxRank);
}

export function chooseUpgrades(
  ranks: UpgradeRanks,
  count = 3,
  random: () => number = Math.random,
): UpgradeDefinition[] {
  const pool = [...availableUpgrades(ranks)];

  for (let index = pool.length - 1; index > 0; index -= 1) {
    const target = Math.floor(random() * (index + 1));
    [pool[index], pool[target]] = [pool[target]!, pool[index]!];
  }

  return pool.slice(0, count);
}

export function chooseUpgradeOffers(
  ranks: UpgradeRanks,
  count = 3,
  random: () => number = Math.random,
): UpgradeOffer[] {
  return chooseUpgrades(ranks, count, random).map((upgrade) => ({
    upgrade,
    tier: rollUpgradeTier(random),
  }));
}

export function applyUpgrade(
  stats: PlayerStats,
  ranks: UpgradeRanks,
  upgrade: UpgradeDefinition,
  tier: UpgradeTier = 'B',
): { stats: PlayerStats; ranks: UpgradeRanks } {
  const currentRank = ranks[upgrade.id] ?? 0;
  if (currentRank >= upgrade.maxRank) return { stats, ranks };

  return {
    stats: upgrade.apply(stats, getTierDefinition(tier).multiplier),
    ranks: { ...ranks, [upgrade.id]: currentRank + 1 },
  };
}

function formatDecimal(value: number): string {
  return value.toFixed(2).replace('.', ',').replace(/,00$/, '');
}

export function describeUpgradeChanges(
  stats: PlayerStats,
  upgrade: UpgradeDefinition,
  tier: UpgradeTier = 'B',
): string[] {
  const next = upgrade.apply(stats, getTierDefinition(tier).multiplier);
  const changes: string[] = [];

  if (stats.hp !== next.hp || stats.maxHp !== next.maxHp) {
    changes.push(
      `HP ${Math.round(stats.hp)}/${Math.round(stats.maxHp)} → ${Math.round(next.hp)}/${Math.round(next.maxHp)}`,
    );
  }
  if (stats.damage !== next.damage) {
    changes.push(`Урон ${Math.round(stats.damage)} → ${Math.round(next.damage)}`);
  }
  if (stats.attackCooldownMs !== next.attackCooldownMs) {
    changes.push(
      `Атак/с ${formatDecimal(1000 / stats.attackCooldownMs)} → ${formatDecimal(1000 / next.attackCooldownMs)}`,
    );
  }
  if (stats.attackRange !== next.attackRange) {
    changes.push(`Радиус ${Math.round(stats.attackRange)} → ${Math.round(next.attackRange)}`);
  }
  if (stats.speed !== next.speed) {
    changes.push(`Скорость ${Math.round(stats.speed)} → ${Math.round(next.speed)}`);
  }
  if (stats.magnetRadius !== next.magnetRadius) {
    changes.push(`Сбор ${Math.round(stats.magnetRadius)} → ${Math.round(next.magnetRadius)}`);
  }
  if (stats.armor !== next.armor) {
    changes.push(`Броня ${Math.round(stats.armor * 100)}% → ${Math.round(next.armor * 100)}%`);
  }

  return changes;
}
